import React, { useState, useRef, useContext } from 'react';
import AppMailReply from './AppMailReply';
import { MailContext } from './Context';
import { Message } from '@microsoft/microsoft-graph-types';
import { SCIcon } from '../../Common/SCIcon';
import { Column, ContextMenu, DataTable, Dialog, ProgressBar } from '../../Common/Imports';
import AppMailDetail from './Detail';
import { useHasPermission, usePageID, usePermissions } from '../../Helpers/Permissions/PermissionContext';
import { AspNetRolesMenuItemsPermissions } from '../../Models/Classes';
import { FormatDateTime } from '../../Common/Utility/FormatDateTime';

interface AppMailTableProps {
    mails: Message[] | undefined;
}

function AppMailTable(props: AppMailTableProps) {
    const { pageID } = usePageID();
    const P_Mail_Delete = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 4 } as AspNetRolesMenuItemsPermissions;

    const [openType, setOpenType] = useState<number>();
    const { onArchive, onDelete, tableIsLoading, selectedFolder, setSelectedMail, selectedMail } = useContext(MailContext);
    const cm = useRef<ContextMenu>(null);
    const dt = useRef<DataTable<Message[]>>(null);
    const [contextElementSelection, setContextElementSelection] = useState<Message | undefined>();
    const hasDeletePermission = useHasPermission(P_Mail_Delete);
    const menuModel = [
        { label: 'View', icon: <SCIcon Name="OpenInNew" Classes="me-2 text-primary" />, command: () => onRowSelect(contextElementSelection, 99) },
        { separator: true },
        { label: 'Reply', icon: <SCIcon Name="Replay" Classes="me-2 text-primary" />, command: () => onRowSelect(contextElementSelection, 0) },
        { label: 'Reply All', icon: <SCIcon Name="ReplayAll" Classes="me-2 text-primary" />, command: () => onRowSelect(contextElementSelection, 1) },
        { label: 'Forward', icon: <SCIcon Name="Forward" Classes="me-2 text-primary" />, command: () => onRowSelect(contextElementSelection, 2) },
        { separator: true },
        ...(hasDeletePermission ? [{ label: 'Delete', icon: <SCIcon Name="Delete" Classes="me-2 text-primary" />, command: () => handleDelete(contextElementSelection?.id) }] : []),
        { label: 'Archive', icon: <SCIcon Name="Archive" Classes="me-2 text-primary" />, command: () => handleArchive(contextElementSelection?.id) },
    ];

    const onRowSelect = (mail: Message | undefined, openType: number) => {
        if (mail) {
            setOpenType(openType);
            setSelectedMail(mail);
        }
    };

    const handleArchive = (id: string | undefined) => {
        if (id) {
            onArchive(id);
        }
    };

    const handleDelete = (id: string | undefined) => {
        if (id) {
            onDelete(id);
        }
    };

    const rowClass = (data: Message) => {
        return {
            'text-primary fw-bold border-start border-primary border-start-5': !data.isRead
        };
    };

    return (
        <React.Fragment>
            {
                selectedMail && openType == 99 &&
                <Dialog
                    maximized
                    visible={!!selectedMail}
                    header={selectedMail.subject}
                    onHide={() => { setSelectedMail(undefined) }}>
                    <AppMailDetail Mail={selectedMail} ShowReservationInfos ShowSend></AppMailDetail>
                </Dialog>
            }
            {
                selectedMail && (openType == 0 || openType == 1 || openType == 2) &&
                <Dialog
                    maximized
                    visible={!!selectedMail}
                    header="New Message"
                    onHide={() => setSelectedMail(undefined)}>
                    <AppMailReply openType={openType} content={selectedMail} hide={() => setSelectedMail(undefined)}></AppMailReply>
                </Dialog>
            }
            <div className="row mb-2">
                <div className="col-md-12">
                    <h4>{selectedFolder?.displayName}</h4>
                </div>
            </div>
            <ContextMenu model={menuModel} ref={cm} onHide={() => setContextElementSelection(undefined)} />
            {
                tableIsLoading ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        ref={dt}
                        value={props.mails}
                        globalFilterFields={['from', 'to', 'title']}
                        emptyMessage="No mails found."
                        rows={20}
                        rowsPerPageOptions={[10, 20, 50]}
                        paginator
                        size="small"
                        rowHover
                        rowClassName={rowClass}
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        onContextMenu={(e) => cm.current?.show(e.originalEvent)}
                        contextMenuSelection={setContextElementSelection}
                        onContextMenuSelectionChange={(e) => setContextElementSelection(e.value)}
                        selection={selectedMail}
                        selectionMode="single"
                        resizableColumns
                        columnResizeMode="expand"
                        onSelectionChange={(e) => { console.log(e); onRowSelect(e.value as Message, 99) }}>
                        <Column
                            style={{ maxWidth: '15rem' }}
                            field="from.emailAddress.address"
                            header="From"
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            style={{ maxWidth: '30rem' }}
                            field="subject"
                            header="Subject"
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            style={{ maxWidth: '15rem' }}
                            header="Date"
                            field="date"
                            body={(e: Message) => { return FormatDateTime(e.receivedDateTime ?? "", { withTime: true }) }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </React.Fragment >
    );
}

export default AppMailTable;
